import { useState, useEffect } from 'react';
import { 
  Trophy, 
  Award, 
  TrendingUp, 
  Users, 
  Zap, 
  Clock, 
  Target,
  Medal,
  Crown
} from 'lucide-react';
import { 
  gamificationApi, 
  formatUptime
} from '../utils/api';
import RelativeTime from './RelativeTime';
import { 
  MonitorGamification, 
  LeaderboardEntry, 
  GlobalStats, 
  Achievement 
} from '../types';

interface GamificationDashboardProps {
  selectedMonitorId?: number;
}

const GamificationDashboard: React.FC<GamificationDashboardProps> = ({ 
  selectedMonitorId 
}) => {
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [recentAchievements, setRecentAchievements] = useState<Achievement[]>([]);
  const [monitorGamification, setMonitorGamification] = useState<MonitorGamification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGamificationData();
  }, [selectedMonitorId]);

  const loadGamificationData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [stats, leaderboardData, achievements] = await Promise.all([
        gamificationApi.getGlobalStats(),
        gamificationApi.getLeaderboard(),
        gamificationApi.getRecentAchievements()
      ]);

      setGlobalStats(stats);
      setLeaderboard(leaderboardData);
      setRecentAchievements(achievements.achievements);

      if (selectedMonitorId) {
        const monitorData = await gamificationApi.getMonitorGamification(selectedMonitorId);
        setMonitorGamification(monitorData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  };

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'streak': return <TrendingUp className="w-5 h-5" />;
      case 'response_time': return <Zap className="w-5 h-5" />;
      case 'total_pings': return <Target className="w-5 h-5" />;
      case 'perfect_period': return <Clock className="w-5 h-5" />;
      default: return <Award className="w-5 h-5" />;
    }
  };

  const getAchievementColor = (type: string) => {
    switch (type) {
      case 'streak': return 'text-blue-500';
      case 'response_time': return 'text-green-500';
      case 'total_pings': return 'text-purple-500';
      case 'perfect_period': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Trophy className="w-8 h-8 animate-pulse mx-auto mb-4" />
          <p>Loading gamification data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <Trophy className="w-5 h-5" />
        <span>Error loading gamification data: {error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Statistics */}
      {globalStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-primary">
              <Trophy className="w-8 h-8" />
            </div>
            <div className="stat-title">Total Achievements</div>
            <div className="stat-value text-primary">{globalStats.total_achievements || 0}</div>
            <div className="stat-desc">Across all monitors</div>
          </div>

          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-success">
              <TrendingUp className="w-8 h-8" />
            </div>
            <div className="stat-title">Best Streak</div>
            <div className="stat-value text-success">{globalStats.top_streak || 0}</div>
            <div className="stat-desc">Consecutive pings</div>
          </div>

          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-info">
              <Target className="w-8 h-8" />
            </div>
            <div className="stat-title">Average Uptime</div>
            <div className="stat-value text-info">{formatUptime(globalStats.average_uptime)}</div>
            <div className="stat-desc">System-wide</div>
          </div>

          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-warning">
              <Users className="w-8 h-8" />
            </div>
            <div className="stat-title">Active Monitors</div>
            <div className="stat-value text-warning">{globalStats.total_monitors || 0}</div>
            <div className="stat-desc">Being tracked</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-6 h-6 text-yellow-500" />
              <h2 className="card-title">Leaderboard</h2>
            </div>
            
            <div className="space-y-3">
              {(leaderboard || []).slice(0, 10).map((entry, index) => (
                <div key={entry.monitor_id} className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-content font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-semibold">{entry.monitor_name}</div>
                      <div className="text-sm opacity-70">
                        {entry.current_streak || 0} current streak • {entry.owner_name || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{formatUptime(entry.uptime_percentage)}</div>
                    <div className="text-sm opacity-70">
                      {entry.total_achievements || 0} achievements
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Achievements */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-6 h-6 text-purple-500" />
              <h2 className="card-title">Recent Achievements</h2>
            </div>
            
            <div className="space-y-3">
              {(recentAchievements || []).slice(0, 8).map((achievement) => (
                <div key={achievement.id} className="flex items-center gap-3 p-3 bg-base-200 rounded-lg">
                  <div className={`${getAchievementColor(achievement.type)}`}>
                    {getAchievementIcon(achievement.type)}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{achievement.title}</div>
                    <div className="text-sm opacity-70">
                      {achievement.description} • {achievement.monitor_name || 'Unknown Monitor'}
                    </div>
                  </div>
                  <div className="text-right text-sm opacity-70">
                    <RelativeTime dateString={achievement.achieved_at} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monitor-specific gamification */}
      {monitorGamification && (
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <Medal className="w-6 h-6 text-blue-500" />
              <h2 className="card-title">{monitorGamification.monitor?.name || 'Unknown Monitor'} - Achievements</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Streak Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Uptime Streak</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="stat bg-base-200 rounded-lg">
                    <div className="stat-title">Current Streak</div>
                    <div className="stat-value text-primary">{monitorGamification.streak?.current_streak || 0}</div>
                  </div>
                  <div className="stat bg-base-200 rounded-lg">
                    <div className="stat-title">Best Streak</div>
                    <div className="stat-value text-success">{monitorGamification.streak?.best_streak || 0}</div>
                  </div>
                </div>
              </div>

              {/* Achievements */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Achievements ({monitorGamification.total_achievements || 0})</h3>
                <div className="space-y-2">
                  {(monitorGamification.achievements || []).slice(0, 5).map((achievement) => (
                    <div key={achievement.id} className="flex items-center gap-3 p-2 bg-base-200 rounded">
                      <div className={`${getAchievementColor(achievement.type)}`}>
                        {getAchievementIcon(achievement.type)}
                      </div>
                      <div>
                        <div className="font-medium">{achievement.title}</div>
                        <div className="text-sm opacity-70">{achievement.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamificationDashboard; 